import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Calendar as CalendarIcon,
  Plus,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  FileText,
  Settings,
  Users,
  Edit,
  Save
} from 'lucide-react';
import { getAuthHeaders } from '../utils/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { API_URL } from '../config';


const LeavePage = () => {
  const { user } = useAuth();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // HR Management states
  const [allBalances, setAllBalances] = useState([]);
  const [accrualRules, setAccrualRules] = useState({});
  const [editingBalance, setEditingBalance] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [showEditBalanceDialog, setShowEditBalanceDialog] = useState(false);
  const [showEditRuleDialog, setShowEditRuleDialog] = useState(false);
  const [coRequests, setCoRequests] = useState([]);
  const [showCoDialog, setShowCoDialog] = useState(false);
  const [coForm, setCoForm] = useState({ worked_dates: '', reason: '' });

  // WFH states
  const [wfhRequests, setWfhRequests] = useState([]);
  const [wfhApprovals, setWfhApprovals] = useState([]);
  const [showWfhDialog, setShowWfhDialog] = useState(false);
  const [wfhSubmitting, setWfhSubmitting] = useState(false);
  const [wfhForm, setWfhForm] = useState({ from_date: null, to_date: null, reason: '' });

  const [leaveForm, setLeaveForm] = useState({
    leave_type_id: '',
    from_date: null,
    to_date: null,
    is_half_day: false,
    half_day_type: '',
    reason: ''
  });

  const isHR = user?.role === 'super_admin' || user?.role === 'hr_admin' || user?.role === 'hr_executive';
  const isAdmin = user?.role === 'super_admin' || user?.role === 'hr_admin';
  const isManagerRole = user?.role === 'manager' || user?.role === 'team_lead' || isHR;
  const [hasApprovals, setHasApprovals] = useState(false);
  const isManager = isManagerRole || hasApprovals;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const [typesRes, balanceRes, requestsRes, rulesRes] = await Promise.all([
        fetch(`${API_URL}/leave-types`, { headers: authHeaders }),
        fetch(`${API_URL}/leave/balance`, { headers: authHeaders }),
        fetch(`${API_URL}/leave/my-requests`, { headers: authHeaders }),
        fetch(`${API_URL}/leave/accrual-rules`, { headers: authHeaders })
      ]);

      if (typesRes.ok) setLeaveTypes(await typesRes.json());
      if (balanceRes.ok) setLeaveBalance(await balanceRes.json());
      if (requestsRes.ok) setMyRequests(await requestsRes.json());
      if (rulesRes.ok) setAccrualRules(await rulesRes.json());

      // Fetch pending approvals for all users (managers see their reportees' requests)
      try {
        const approvalsRes = await fetch(`${API_URL}/leave/pending-approvals`, { headers: authHeaders });
        if (approvalsRes.ok) {
          const approvals = await approvalsRes.json();
          setPendingApprovals(approvals);
          if (approvals.length > 0) setHasApprovals(true);
        }
      } catch (e) {
        // Not authorized to see approvals - that's OK
      }
      
      // Fetch HR management data (all balances)
      if (isHR) {
        const allBalancesRes = await fetch(`${API_URL}/leave/balances/all`, { headers: authHeaders });
        if (allBalancesRes.ok) setAllBalances(await allBalancesRes.json());
      }

      // Fetch CO requests
      const coRes = await fetch(`${API_URL}/co-requests`, { headers: authHeaders });
      if (coRes.ok) setCoRequests(await coRes.json());

      // Fetch WFH requests
      const wfhRes = await fetch(`${API_URL}/wfh/my-requests`, { headers: authHeaders });
      if (wfhRes.ok) setWfhRequests(await wfhRes.json());

      // Fetch WFH pending approvals
      try {
        const wfhApprovalsRes = await fetch(`${API_URL}/wfh/pending-approvals`, { headers: authHeaders });
        if (wfhApprovalsRes.ok) {
          const wfhApps = await wfhApprovalsRes.json();
          setWfhApprovals(wfhApps);
        }
      } catch (e) { /* Not authorized */ }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelLeave = async (leaveId) => {
    try {
      const res = await fetch(`${API_URL}/leave/${leaveId}/cancel`, {
        method: 'PUT', headers: getAuthHeaders(), 
      });
      if (res.ok) { toast.success('Leave request cancelled'); fetchData(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed to cancel'); }
    } catch { toast.error('Failed to cancel'); }
  };

  const handleSubmitCO = async () => {
    const dates = coForm.worked_dates.split(',').map(d => d.trim()).filter(Boolean);
    if (dates.length === 0) { toast.error('Enter at least one worked date'); return; }
    try {
      const res = await fetch(`${API_URL}/co-requests`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ worked_dates: dates, reason: coForm.reason }),
      });
      if (res.ok) { toast.success('CO request submitted'); setShowCoDialog(false); setCoForm({ worked_dates: '', reason: '' }); fetchData(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Failed to submit'); }
  };

  const handleApproveCO = async (coId) => {
    try {
      const res = await fetch(`${API_URL}/co-requests/${coId}/approve`, {
        method: 'PUT', headers: getAuthHeaders(), 
      });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchData(); }
    } catch { toast.error('Failed'); }
  };

  const handleRejectCO = async (coId) => {
    try {
      await fetch(`${API_URL}/co-requests/${coId}/reject`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ reason: 'Rejected' }),
      });
      toast.success('CO rejected'); fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleApplyWfh = async () => {
    if (!wfhForm.from_date || !wfhForm.to_date || !wfhForm.reason) {
      toast.error('Please fill all required fields');
      return;
    }
    setWfhSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/wfh/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          from_date: format(wfhForm.from_date, 'yyyy-MM-dd'),
          to_date: format(wfhForm.to_date, 'yyyy-MM-dd'),
          reason: wfhForm.reason
        })
      });
      if (res.ok) {
        toast.success('WFH request submitted');
        setShowWfhDialog(false);
        setWfhForm({ from_date: null, to_date: null, reason: '' });
        fetchData();
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Failed to submit');
      }
    } catch { toast.error('Failed to submit WFH request'); }
    finally { setWfhSubmitting(false); }
  };

  const handleCancelWfh = async (wfhId) => {
    try {
      const res = await fetch(`${API_URL}/wfh/${wfhId}/cancel`, {
        method: 'PUT', headers: getAuthHeaders(), 
      });
      if (res.ok) { toast.success('WFH request cancelled'); fetchData(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Failed to cancel'); }
  };

  const handleApproveWfh = async (wfhId) => {
    try {
      const res = await fetch(`${API_URL}/wfh/${wfhId}/approve`, {
        method: 'PUT', headers: getAuthHeaders(), 
      });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchData(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Failed to approve'); }
  };

  const handleRejectWfh = async (wfhId) => {
    const reason = prompt('Rejection reason:');
    if (reason === null) return;
    try {
      const res = await fetch(`${API_URL}/wfh/${wfhId}/reject?rejection_reason=${encodeURIComponent(reason)}`, {
        method: 'PUT', headers: getAuthHeaders(), 
      });
      if (res.ok) { toast.success('WFH request rejected'); fetchData(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Failed to reject'); }
  };

  const handleApplyLeave = async () => {
    if (!leaveForm.leave_type_id || !leaveForm.from_date || !leaveForm.to_date || !leaveForm.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/leave/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          ...leaveForm,
          from_date: format(leaveForm.from_date, 'yyyy-MM-dd'),
          to_date: format(leaveForm.to_date, 'yyyy-MM-dd')
        })
      });

      if (response.ok) {
        toast.success('Leave applied successfully');
        setShowApplyDialog(false);
        setLeaveForm({
          leave_type_id: '',
          from_date: null,
          to_date: null,
          is_half_day: false,
          half_day_type: '',
          reason: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to apply leave');
      }
    } catch (error) {
      toast.error('Failed to apply leave');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveLeave = async (leaveId) => {
    try {
      const response = await fetch(`${API_URL}/leave/${leaveId}/approve`, {
        method: 'PUT',
        
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Leave approved');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve leave');
      }
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const handleRejectLeave = async (leaveId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      const response = await fetch(`${API_URL}/leave/${leaveId}/reject?rejection_reason=${encodeURIComponent(reason)}`, {
        method: 'PUT',
        
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Leave rejected');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reject leave');
      }
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  // HR Management Functions
  const handleEditBalance = (employee, balance) => {
    setEditingBalance({
      employee_id: employee.employee_id,
      emp_code: employee.emp_code,
      employee_name: employee.employee_name,
      leave_type_id: balance.leave_type_id,
      opening_balance: balance.opening_balance || 0,
      accrued: balance.accrued || 0,
      used: balance.used || 0,
      pending: balance.pending || 0,
      available: balance.available || 0
    });
    setShowEditBalanceDialog(true);
  };

  const handleSaveBalance = async () => {
    if (!editingBalance) return;
    
    try {
      const response = await fetch(`${API_URL}/leave/balances/${editingBalance.employee_id}`, {
        method: 'PUT',
        
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editingBalance)
      });

      if (response.ok) {
        toast.success('Leave balance updated');
        setShowEditBalanceDialog(false);
        setEditingBalance(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update balance');
      }
    } catch (error) {
      toast.error('Failed to update balance');
    }
  };

  const handleEditRule = (code, rule) => {
    setEditingRule({ code, ...rule });
    setShowEditRuleDialog(true);
  };

  const handleSaveRule = async () => {
    if (!editingRule) return;
    
    const { code, ...ruleData } = editingRule;
    
    try {
      const response = await fetch(`${API_URL}/leave/accrual-rules/${code}`, {
        method: 'PUT',
        
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(ruleData)
      });

      if (response.ok) {
        toast.success(`Leave rule for ${code} updated`);
        setShowEditRuleDialog(false);
        setEditingRule(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update rule');
      }
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const handleRunAccrual = async () => {
    if (!window.confirm('Run leave accrual for current month? This will add accrued leaves to all eligible employees.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/leave/run-accrual`, {
        method: 'POST',
        
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Accrual completed: ${result.accruals_processed} records processed`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to run accrual');
      }
    } catch (error) {
      toast.error('Failed to run accrual');
    }
  };

  const getLeaveTypeName = (typeId) => {
    const lt = leaveTypes.find(t => t.leave_type_id === typeId);
    return lt?.name || typeId;
  };

  const statusColors = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    cancelled: 'bg-slate-100 text-slate-600 border-slate-200'
  };

  const statusIcons = {
    pending: AlertCircle,
    approved: CheckCircle2,
    rejected: XCircle,
    cancelled: XCircle
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leave-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <span className="section-pill mono-accent">// Leave Management</span>
            <h1 className="text-2xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Leave Management
          </h1>
          <p className="text-slate-600 mt-1">Apply for leave and track your requests</p>
            <div className="header-accent-line mt-3 max-w-[160px]" />
        </div>
        <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="apply-leave-btn">
              <Plus className="w-4 h-4" />
              Apply Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Apply for Leave</DialogTitle>
              <DialogDescription>
                Submit your leave request for approval
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Leave Type *</Label>
                <Select
                  value={leaveForm.leave_type_id}
                  onValueChange={(value) => setLeaveForm({ ...leaveForm, leave_type_id: value })}
                >
                  <SelectTrigger data-testid="select-leave-type">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    {leaveTypes.map(lt => (
                      <SelectItem key={lt.leave_type_id} value={lt.leave_type_id}>
                        {lt.name} ({lt.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="from-date-btn">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {leaveForm.from_date ? format(leaveForm.from_date, 'dd/MM/yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={leaveForm.from_date}
                        onSelect={(date) => setLeaveForm({ ...leaveForm, from_date: date, to_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>To Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="to-date-btn">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {leaveForm.to_date ? format(leaveForm.to_date, 'dd/MM/yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={leaveForm.to_date}
                        onSelect={(date) => setLeaveForm({ ...leaveForm, to_date: date })}
                        disabled={(date) => leaveForm.from_date && date < leaveForm.from_date}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="half-day">Half Day Leave</Label>
                <Switch
                  id="half-day"
                  checked={leaveForm.is_half_day}
                  onCheckedChange={(checked) => setLeaveForm({ ...leaveForm, is_half_day: checked })}
                />
              </div>

              {leaveForm.is_half_day && (
                <div className="space-y-2">
                  <Label>Half Day Type</Label>
                  <Select
                    value={leaveForm.half_day_type}
                    onValueChange={(value) => setLeaveForm({ ...leaveForm, half_day_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select half" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="first_half">First Half</SelectItem>
                      <SelectItem value="second_half">Second Half</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Reason *</Label>
                <Textarea
                  value={leaveForm.reason}
                  onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                  placeholder="Enter reason for leave..."
                  rows={3}
                  data-testid="leave-reason"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApplyDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleApplyLeave} disabled={submitting} data-testid="submit-leave-btn">
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Leave Balance Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {leaveTypes.slice(0, 6).map(lt => {
          const balance = leaveBalance.find(b => b.leave_type_id === lt.leave_type_id) || {};
          // Get quota from accrual rules if available (HR-configured), otherwise fall back to leave type default
          const ruleCode = lt.code?.toUpperCase() || lt.leave_type_id?.replace('lt_', '').toUpperCase();
          const ruleQuota = accrualRules[ruleCode]?.annual_quota;
          const quota = ruleQuota !== undefined ? ruleQuota : lt.annual_quota;
          
          const available = balance.available !== undefined ? balance.available : quota;
          const total = (balance.opening_balance !== undefined && balance.accrued !== undefined) 
            ? (balance.opening_balance + balance.accrued) 
            : quota;
          
          return (
            <Card key={lt.leave_type_id} className="card-hover" data-testid={`balance-${lt.code}`}>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-slate-500 mb-1">{lt.code}</p>
                <p className="text-2xl font-bold text-slate-900">{available}</p>
                <p className="text-xs text-slate-500 mb-2">of {total} days</p>
                <Progress value={(available / (total || 1)) * 100} className="h-1.5" />
              </CardContent>
            </Card>
          );
        })}
      </div>


      {/* Tabs for Requests */}
      <Tabs defaultValue="my-requests" className="space-y-4">
        <TabsList className="bg-white border">
          <TabsTrigger value="my-requests" data-testid="tab-my-requests">
            My Requests
            {myRequests.filter(r => r.status === 'pending').length > 0 && (
              <Badge className="ml-2 h-5 px-1.5">{myRequests.filter(r => r.status === 'pending').length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="co-requests" data-testid="tab-co-requests">
            CO Requests
          </TabsTrigger>
          <TabsTrigger value="wfh" data-testid="tab-wfh">
            Work From Home
            {wfhRequests.filter(r => r.status === 'pending').length > 0 && (
              <Badge className="ml-2 h-5 px-1.5">{wfhRequests.filter(r => r.status === 'pending').length}</Badge>
            )}
          </TabsTrigger>
          {(isManager || wfhApprovals.length > 0) && (
            <TabsTrigger value="approvals" data-testid="tab-approvals">
              Pending Approvals
              {(pendingApprovals.length + wfhApprovals.length) > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5">{pendingApprovals.length + wfhApprovals.length}</Badge>
              )}
            </TabsTrigger>
          )}
          {isHR && (
            <TabsTrigger value="manage-balances" data-testid="tab-manage-balances">
              <Users className="w-4 h-4 mr-1" />
              Manage Balances
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="leave-rules" data-testid="tab-leave-rules">
              <Settings className="w-4 h-4 mr-1" />
              Leave Rules
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="my-requests">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                My Leave Requests
              </CardTitle>
              <CardDescription>Track the status of your leave applications</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {myRequests.length > 0 ? (
                  myRequests.map(request => {
                    const StatusIcon = statusIcons[request.status];
                    return (
                      <div 
                        key={request.leave_id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                        data-testid={`leave-request-${request.leave_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            request.status === 'approved' ? 'bg-emerald-100' :
                            request.status === 'rejected' ? 'bg-red-100' : 'bg-amber-100'
                          }`}>
                            <StatusIcon className={`w-5 h-5 ${
                              request.status === 'approved' ? 'text-emerald-600' :
                              request.status === 'rejected' ? 'text-red-600' : 'text-amber-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {getLeaveTypeName(request.leave_type_id)}
                            </p>
                            <p className="text-sm text-slate-500">
                              {request.from_date} to {request.to_date} ({request.days} {request.days === 1 ? 'day' : 'days'})
                            </p>
                            <p className="text-xs text-slate-400 mt-1">{request.reason}</p>
                            {request.dept_head_status && request.dept_head_status !== 'not_required' && (
                              <div className="flex gap-2 mt-1.5">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${request.dept_head_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  Manager: {request.dept_head_status}
                                </span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${request.hr_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  HR: {request.hr_status || 'pending'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {request.status === 'pending' && (
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleCancelLeave(request.leave_id)} data-testid={`cancel-leave-${request.leave_id}`}>
                              <XCircle className="w-4 h-4 mr-1" /> Cancel
                            </Button>
                          )}
                          <Badge className={statusColors[request.status]}>
                            {request.status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No leave requests yet</p>
                    <Button className="mt-4" onClick={() => setShowApplyDialog(true)}>
                      Apply for Leave
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CO Requests Tab */}
        <TabsContent value="co-requests">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Compensatory Off Requests</CardTitle>
                <CardDescription>Request CO for working on weekends/holidays</CardDescription>
              </div>
              <Button onClick={() => setShowCoDialog(true)} data-testid="request-co-btn">
                <Plus className="w-4 h-4 mr-2" /> Request CO
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {coRequests.length > 0 ? coRequests.map(co => (
                  <div key={co.co_request_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid={`co-${co.co_request_id}`}>
                    <div>
                      <p className="font-medium text-slate-900">
                        {co.employee_name || 'You'} — {co.days} day(s) CO
                      </p>
                      <p className="text-sm text-slate-500">Worked: {co.worked_dates?.join(', ')}</p>
                      {co.reason && <p className="text-xs text-slate-400">{co.reason}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      {co.status === 'pending' && co.employee_id === user?.employee_id && (
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => {
                          fetch(`${API_URL}/co-requests/${co.co_request_id}/cancel`, { method: 'PUT', headers: getAuthHeaders(),  }).then(() => { toast.success('Cancelled'); fetchData(); });
                        }}>Cancel</Button>
                      )}
                      {co.status === 'pending' && (isManager || isHR) && co.employee_id !== user?.employee_id && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleApproveCO(co.co_request_id)}>Approve</Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRejectCO(co.co_request_id)}>Reject</Button>
                        </>
                      )}
                      <Badge className={statusColors[co.status] || 'bg-slate-100 text-slate-700'}>{co.status}</Badge>
                    </div>
                  </div>
                )) : (
                  <p className="text-center py-8 text-slate-400">No CO requests</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WFH Tab */}
        <TabsContent value="wfh">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Work From Home Requests
                  </CardTitle>
                  <CardDescription>Apply for and track your WFH requests</CardDescription>
                </div>
                <Button onClick={() => setShowWfhDialog(true)} data-testid="apply-wfh-btn">
                  <Plus className="w-4 h-4 mr-1" /> Apply WFH
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {wfhRequests.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No WFH requests yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Applied On</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wfhRequests.map(req => (
                      <TableRow key={req.wfh_id} data-testid={`wfh-row-${req.wfh_id}`}>
                        <TableCell>{req.from_date}</TableCell>
                        <TableCell>{req.to_date}</TableCell>
                        <TableCell>{req.days}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{req.reason}</TableCell>
                        <TableCell>
                          <Badge variant={
                            req.status === 'approved' ? 'default' :
                            req.status === 'rejected' ? 'destructive' :
                            req.status === 'cancelled' ? 'outline' : 'secondary'
                          } data-testid={`wfh-status-${req.wfh_id}`}>
                            {req.status === 'pending' && req.dept_head_status === 'approved' ? 'Manager Approved' : req.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {req.applied_on ? new Date(req.applied_on).toLocaleDateString() : ''}
                        </TableCell>
                        <TableCell>
                          {req.status === 'pending' && (
                            <Button variant="ghost" size="sm" onClick={() => handleCancelWfh(req.wfh_id)} data-testid={`cancel-wfh-${req.wfh_id}`}>
                              <XCircle className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {(isManager || wfhApprovals.length > 0) && (
          <TabsContent value="approvals">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Pending Approvals
                </CardTitle>
                <CardDescription>Leave requests awaiting your approval</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingApprovals.length > 0 ? (
                    pendingApprovals.map(request => (
                      <div 
                        key={request.leave_id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50 rounded-lg gap-4"
                        data-testid={`approval-${request.leave_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {request.employee_name || request.employee_id}
                              {request.emp_code && <span className="text-xs text-slate-400 ml-2">({request.emp_code})</span>}
                            </p>
                            <p className="text-sm text-primary font-medium mt-0.5">
                              {getLeaveTypeName(request.leave_type_id)}
                            </p>
                            <p className="text-sm text-slate-500">
                              {request.from_date} to {request.to_date} ({request.days} days)
                            </p>
                            <p className="text-xs text-slate-400 mt-1">{request.reason}</p>
                            {request.dept_head_status && request.dept_head_status !== 'not_required' && (
                              <div className="flex gap-2 mt-1.5">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${request.dept_head_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  Manager: {request.dept_head_status}
                                </span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${request.hr_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  HR: {request.hr_status || 'pending'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 sm:flex-shrink-0">
                          <Button
                            size="sm"
                            onClick={() => handleApproveLeave(request.leave_id)}
                            className="gap-1"
                            data-testid={`approve-${request.leave_id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRejectLeave(request.leave_id)}
                            className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`reject-${request.leave_id}`}
                          >
                            <XCircle className="w-4 h-4" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                      <p className="text-slate-500">No pending leave approvals</p>
                    </div>
                  )}
                </div>

                {/* WFH Approvals */}
                {wfhApprovals.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" /> WFH Requests ({wfhApprovals.length})
                    </h3>
                    <div className="space-y-3">
                      {wfhApprovals.map(req => (
                        <div
                          key={req.wfh_id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-blue-50 rounded-lg gap-4"
                          data-testid={`wfh-approval-${req.wfh_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {req.employee_name || req.employee_id}
                                {req.emp_code && <span className="text-xs text-slate-400 ml-2">({req.emp_code})</span>}
                              </p>
                              <p className="text-sm text-blue-600 font-medium mt-0.5">Work From Home</p>
                              <p className="text-sm text-slate-500">
                                {req.from_date} to {req.to_date} ({req.days} day{req.days > 1 ? 's' : ''})
                              </p>
                              <p className="text-xs text-slate-400 mt-1">{req.reason}</p>
                              {req.dept_head_status && req.dept_head_status !== 'not_required' && (
                                <div className="flex gap-2 mt-1.5">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${req.dept_head_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                    Manager: {req.dept_head_status}
                                  </span>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${req.hr_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                    HR: {req.hr_status || 'pending'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 sm:flex-shrink-0">
                            <Button size="sm" onClick={() => handleApproveWfh(req.wfh_id)} className="gap-1" data-testid={`approve-wfh-${req.wfh_id}`}>
                              <CheckCircle2 className="w-4 h-4" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleRejectWfh(req.wfh_id)}
                              className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`reject-wfh-${req.wfh_id}`}>
                              <XCircle className="w-4 h-4" /> Reject
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* HR: Manage Balances Tab */}
        {isHR && (
          <TabsContent value="manage-balances">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Employee Leave Balances
                    </CardTitle>
                    <CardDescription>View and edit leave balances for all employees</CardDescription>
                  </div>
                  <Button onClick={handleRunAccrual} variant="outline" size="sm" className="gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Run Monthly Accrual
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Emp Code</TableHead>
                        <TableHead className="text-center">CL</TableHead>
                        <TableHead className="text-center">SL</TableHead>
                        <TableHead className="text-center">EL</TableHead>
                        <TableHead className="text-center">CO</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allBalances.length > 0 ? (
                        allBalances.map(emp => {
                          const getBalance = (typeId) => emp.balances?.find(b => b.leave_type_id === typeId) || {};
                          return (
                            <TableRow key={emp.employee_id}>
                              <TableCell className="font-medium">{emp.employee_name}</TableCell>
                              <TableCell>{emp.emp_code}</TableCell>
                              <TableCell className="text-center">
                                <span className="text-emerald-600 font-medium">{getBalance('lt_cl').available || 0}</span>
                                <span className="text-slate-400 text-xs">/{getBalance('lt_cl').opening_balance || 0}</span>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="text-emerald-600 font-medium">{getBalance('lt_sl').available || 0}</span>
                                <span className="text-slate-400 text-xs">/{getBalance('lt_sl').opening_balance || 0}</span>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="text-emerald-600 font-medium">{getBalance('lt_el').available || 0}</span>
                                <span className="text-slate-400 text-xs">/{getBalance('lt_el').opening_balance || 0}</span>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="text-emerald-600 font-medium">{getBalance('lt_co').available || 0}</span>
                                <span className="text-slate-400 text-xs">/{getBalance('lt_co').opening_balance || 0}</span>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex gap-1 justify-center">
                                  {['lt_cl', 'lt_sl', 'lt_el', 'lt_co'].map(typeId => {
                                    const balance = getBalance(typeId);
                                    return (
                                      <Button
                                        key={typeId}
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 w-7 p-0"
                                        onClick={() => handleEditBalance(emp, { ...balance, leave_type_id: typeId })}
                                        title={`Edit ${typeId.replace('lt_', '').toUpperCase()}`}
                                      >
                                        <Edit className="w-3 h-3" />
                                      </Button>
                                    );
                                  })}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                            No employee balances found. Import leave balances from Bulk Import section.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Admin: Leave Rules Tab */}
        {isAdmin && (
          <TabsContent value="leave-rules">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Leave Accrual Rules
                </CardTitle>
                <CardDescription>Configure how employees earn and use different types of leaves</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {Object.entries(accrualRules).map(([code, rule]) => (
                    <div key={code} className="border rounded-lg p-4 bg-slate-50">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold text-slate-900">{rule.name || code}</h4>
                          <p className="text-sm text-slate-500">Code: {code}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditRule(code, rule)}
                          className="gap-1"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Annual Quota:</span>
                          <span className="ml-2 font-medium">{rule.annual_quota || 0} days</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Accrual Type:</span>
                          <span className="ml-2 font-medium capitalize">{rule.accrual_type || 'None'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Accrual Rate:</span>
                          <span className="ml-2 font-medium">{rule.accrual_rate || 0}/period</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Carry Forward:</span>
                          <span className={`ml-2 font-medium ${rule.carry_forward ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {rule.carry_forward ? `Yes (max ${rule.max_carry_forward})` : 'No'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500">Encashment:</span>
                          <span className={`ml-2 font-medium ${rule.encashment_allowed ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {rule.encashment_allowed ? 'Allowed' : 'Not Allowed'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500">Probation:</span>
                          <span className={`ml-2 font-medium ${rule.probation_eligible ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {rule.probation_eligible ? 'Eligible' : 'Not Eligible'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500">Max Consecutive:</span>
                          <span className="ml-2 font-medium">{rule.max_consecutive_days || '-'} days</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Advance Notice:</span>
                          <span className="ml-2 font-medium">{rule.advance_notice_days || 0} days</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {Object.keys(accrualRules).length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      No leave rules configured. Default rules will be applied.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Edit Balance Dialog */}
      <Dialog open={showEditBalanceDialog} onOpenChange={setShowEditBalanceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Edit Leave Balance
            </DialogTitle>
            <DialogDescription>
              {editingBalance?.employee_name} ({editingBalance?.emp_code}) - {editingBalance?.leave_type_id?.replace('lt_', '').toUpperCase()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Opening Balance</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={editingBalance?.opening_balance || 0}
                  onChange={(e) => setEditingBalance({
                    ...editingBalance,
                    opening_balance: parseFloat(e.target.value) || 0
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Accrued</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={editingBalance?.accrued || 0}
                  onChange={(e) => setEditingBalance({
                    ...editingBalance,
                    accrued: parseFloat(e.target.value) || 0
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Used</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={editingBalance?.used || 0}
                  onChange={(e) => setEditingBalance({
                    ...editingBalance,
                    used: parseFloat(e.target.value) || 0
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Pending</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={editingBalance?.pending || 0}
                  onChange={(e) => setEditingBalance({
                    ...editingBalance,
                    pending: parseFloat(e.target.value) || 0
                  })}
                />
              </div>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg">
              <span className="text-sm text-slate-500">Available Balance: </span>
              <span className="font-semibold text-emerald-600">
                {((editingBalance?.opening_balance || 0) + (editingBalance?.accrued || 0) - (editingBalance?.used || 0) - (editingBalance?.pending || 0)).toFixed(1)} days
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditBalanceDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveBalance} className="gap-2">
              <Save className="w-4 h-4" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Rule Dialog */}
      <Dialog open={showEditRuleDialog} onOpenChange={setShowEditRuleDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Edit Leave Rule - {editingRule?.name || editingRule?.code}
            </DialogTitle>
            <DialogDescription>
              Configure accrual and usage rules for this leave type
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Basic Settings */}
            <div>
              <h4 className="font-medium mb-3 text-slate-900">Basic Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Leave Name</Label>
                  <Input
                    value={editingRule?.name || ''}
                    onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Annual Quota (days)</Label>
                  <Input
                    type="number"
                    value={editingRule?.annual_quota || 0}
                    onChange={(e) => setEditingRule({ ...editingRule, annual_quota: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </div>

            {/* Accrual Settings */}
            <div>
              <h4 className="font-medium mb-3 text-slate-900">Accrual Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Accrual Type</Label>
                  <Select
                    value={editingRule?.accrual_type || 'none'}
                    onValueChange={(value) => setEditingRule({ ...editingRule, accrual_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (Manual)</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly (Credited at start)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Accrual Rate (per period)</Label>
                  <Input
                    type="number"
                    step="0.25"
                    value={editingRule?.accrual_rate || 0}
                    onChange={(e) => setEditingRule({ ...editingRule, accrual_rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </div>

            {/* Carry Forward & Encashment */}
            <div>
              <h4 className="font-medium mb-3 text-slate-900">Carry Forward & Encashment</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="carry_forward"
                    checked={editingRule?.carry_forward || false}
                    onChange={(e) => setEditingRule({ ...editingRule, carry_forward: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="carry_forward">Allow Carry Forward</Label>
                </div>
                <div className="space-y-2">
                  <Label>Max Carry Forward (days)</Label>
                  <Input
                    type="number"
                    value={editingRule?.max_carry_forward || 0}
                    onChange={(e) => setEditingRule({ ...editingRule, max_carry_forward: parseInt(e.target.value) || 0 })}
                    disabled={!editingRule?.carry_forward}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="encashment_allowed"
                    checked={editingRule?.encashment_allowed || false}
                    onChange={(e) => setEditingRule({ ...editingRule, encashment_allowed: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="encashment_allowed">Allow Encashment</Label>
                </div>
                <div className="space-y-2">
                  <Label>Encashment Rate (%)</Label>
                  <Input
                    type="number"
                    value={editingRule?.encashment_rate || 0}
                    onChange={(e) => setEditingRule({ ...editingRule, encashment_rate: parseInt(e.target.value) || 0 })}
                    disabled={!editingRule?.encashment_allowed}
                  />
                </div>
              </div>
            </div>

            {/* Eligibility & Restrictions */}
            <div>
              <h4 className="font-medium mb-3 text-slate-900">Eligibility & Restrictions</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="probation_eligible"
                    checked={editingRule?.probation_eligible || false}
                    onChange={(e) => setEditingRule({ ...editingRule, probation_eligible: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="probation_eligible">Probation Eligible</Label>
                </div>
                <div className="space-y-2">
                  <Label>Min Service Days Required</Label>
                  <Input
                    type="number"
                    value={editingRule?.min_service_days || 0}
                    onChange={(e) => setEditingRule({ ...editingRule, min_service_days: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Consecutive Days</Label>
                  <Input
                    type="number"
                    value={editingRule?.max_consecutive_days || 0}
                    onChange={(e) => setEditingRule({ ...editingRule, max_consecutive_days: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Advance Notice (days)</Label>
                  <Input
                    type="number"
                    value={editingRule?.advance_notice_days || 0}
                    onChange={(e) => setEditingRule({ ...editingRule, advance_notice_days: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="can_be_half_day"
                    checked={editingRule?.can_be_half_day || false}
                    onChange={(e) => setEditingRule({ ...editingRule, can_be_half_day: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="can_be_half_day">Allow Half Day</Label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRuleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRule} className="gap-2">
              <Save className="w-4 h-4" />
              Save Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CO Request Dialog */}
      <Dialog open={showCoDialog} onOpenChange={setShowCoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Compensatory Off</DialogTitle>
            <DialogDescription>Enter the dates you worked on weekends/holidays</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Worked Date(s) *</Label>
              <Input type="date" value={coForm.worked_dates} onChange={e => setCoForm({...coForm, worked_dates: e.target.value})} placeholder="Select date" data-testid="co-date-input" />
              <p className="text-xs text-slate-400">For multiple dates, enter comma-separated: 2026-02-08, 2026-02-15</p>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={coForm.reason} onChange={e => setCoForm({...coForm, reason: e.target.value})} placeholder="e.g., Worked on Sunday for project deadline" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCoDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitCO} data-testid="submit-co-btn">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WFH Apply Dialog */}
      <Dialog open={showWfhDialog} onOpenChange={setShowWfhDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apply for Work From Home</DialogTitle>
            <DialogDescription>Select dates and provide a reason for your WFH request</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>From Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="wfh-from-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {wfhForm.from_date ? format(wfhForm.from_date, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={wfhForm.from_date} onSelect={d => setWfhForm({...wfhForm, from_date: d, to_date: wfhForm.to_date && d > wfhForm.to_date ? d : wfhForm.to_date})} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>To Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="wfh-to-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {wfhForm.to_date ? format(wfhForm.to_date, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={wfhForm.to_date} onSelect={d => setWfhForm({...wfhForm, to_date: d})} disabled={d => wfhForm.from_date && d < wfhForm.from_date} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea
                value={wfhForm.reason}
                onChange={e => setWfhForm({...wfhForm, reason: e.target.value})}
                placeholder="e.g., Need to work from home for personal reasons"
                rows={3}
                data-testid="wfh-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWfhDialog(false)}>Cancel</Button>
            <Button onClick={handleApplyWfh} disabled={wfhSubmitting} data-testid="submit-wfh-btn">
              {wfhSubmitting ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : null}
              Submit WFH Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeavePage;
